/**
 * Share-target capability (src/platform/) — inbound media shared from the
 * OS share sheet into the app.
 *
 * Contract: every capability has a working web implementation; on web the
 * share-target seam is a no-op (browsers deliver shared files via the
 * deferred PWA share_target manifest instead, #978/#993). On Android the
 * custom ShareTarget Capacitor plugin (frontend/android — ShareTargetPlugin.java)
 * copies shared content:// URIs into app cache and hands back file paths;
 * this module turns them into File objects for the upload pipeline. On iOS
 * the ShareExtension target hands shared media to the main app via the
 * app-group container, and the Swift ShareTargetPlugin (frontend/ios —
 * ShareTargetPlugin.swift) drains it into app cache with the same contract.
 */

import { isNativePlatform } from './detection'

interface SharedItem {
  path: string
  name: string
  mimeType: string
}

interface SharedItems {
  items: SharedItem[]
}

interface ShareTargetPluginApi {
  consumePending(): Promise<SharedItems>
  addListener(
    eventName: 'shareReceived',
    listener: (data: SharedItems) => void
  ): Promise<{ remove: () => Promise<void> }>
}

async function toFiles(
  items: SharedItem[],
  convertFileSrc: (path: string) => string
): Promise<File[]> {
  const files: File[] = []
  for (const item of items) {
    try {
      const response = await fetch(convertFileSrc(item.path))
      const blob = await response.blob()
      files.push(new File([blob], item.name, { type: item.mimeType || blob.type }))
    } catch {
      // Skip unreadable items; the rest of the share still goes through.
    }
  }
  return files
}

/**
 * Start listening for media shared into the app from the OS share sheet.
 * Consumes anything already pending from the launch intent, then keeps
 * listening for shares that arrive while the app is running. No-op on web
 * and on platforms without the ShareTarget plugin.
 */
export async function initShareTarget(onFiles: (files: File[]) => void): Promise<void> {
  if (!isNativePlatform()) return

  const { Capacitor, registerPlugin } = await import('@capacitor/core')
  const shareTarget = registerPlugin<ShareTargetPluginApi>('ShareTarget')

  const deliver = async (items: SharedItem[]) => {
    if (items.length === 0) return
    const files = await toFiles(items, Capacitor.convertFileSrc)
    if (files.length > 0) onFiles(files)
  }

  try {
    await shareTarget.addListener('shareReceived', (data) => void deliver(data.items))
    const pending = await shareTarget.consumePending()
    await deliver(pending.items)
  } catch {
    // Plugin not implemented on this platform — the capability is simply
    // absent.
  }
}
